import qs from 'qs';

import { AppDispatch } from 'types';
import { httpGet, httpPost } from 'utils';
import { buildStringifiedQuery } from 'utils/apiHelpers';
import { openFlashMessage } from 'components/utils/flashMessages';

import {
  SEARCH_JOBS_REQUEST,
  SEARCH_JOBS_SUCCESS,
  SEARCH_JOBS_ERROR,
  FETCH_BULLHORN_JOB_REQUEST,
  FETCH_BULLHORN_JOB_SUCCESS,
  FETCH_BULLHORN_JOB_ERROR,
  FETCH_BULLHORN_JOB_PREFILL_REQUEST,
  FETCH_BULLHORN_JOB_PREFILL_SUCCESS,
  FETCH_BULLHORN_JOB_PREFILL_ERROR,
  CANDIDATES_BULLHORN_JOB_SUBMISSIONS_CREATE_REQUEST,
  CANDIDATES_BULLHORN_JOB_SUBMISSIONS_CREATE_SUCCESS,
  CANDIDATES_BULLHORN_JOB_SUBMISSIONS_CREATE_ERROR,
  CANDIDATES_BULLHORN_JOB_SUBMISSIONS_SEARCH_REQUEST,
  CANDIDATES_BULLHORN_JOB_SUBMISSIONS_SEARCH_SUCCESS,
  CANDIDATES_BULLHORN_JOB_SUBMISSIONS_SEARCH_ERROR,
  CANDIDATES_BULLHORN_TEAR_SHEETS_SEARCH_REQUEST,
  CANDIDATES_BULLHORN_TEAR_SHEETS_SEARCH_SUCCESS,
  CANDIDATES_BULLHORN_TEAR_SHEETS_SEARCH_ERROR,
  CANDIDATES_BULLHORN_TEAR_SHEETS_ADD_CANDIDATE_REQUEST,
  CANDIDATES_BULLHORN_TEAR_SHEETS_ADD_CANDIDATE_SUCCESS,
  CANDIDATES_BULLHORN_TEAR_SHEETS_ADD_CANDIDATE_ERROR,
  CANDIDATES_BULLHORN_TEAR_SHEETS_CREATE_REQUEST,
  CANDIDATES_BULLHORN_TEAR_SHEETS_CREATE_SUCCESS,
  CANDIDATES_BULLHORN_TEAR_SHEETS_CREATE_ERROR,
  CREATE_CANDIDATE_SEARCH_FROM_BULLHORN_JOB_REQUEST,
  CREATE_CANDIDATE_SEARCH_FROM_BULLHORN_JOB_SUCCESS,
  CREATE_CANDIDATE_SEARCH_FROM_BULLHORN_JOB_ERROR,
  CANDIDATES_BULLHORN_BULK_JOB_SUBMISSIONS_CREATE_REQUEST,
  CANDIDATES_BULLHORN_BULK_JOB_SUBMISSIONS_CREATE_SUCCESS,
  CANDIDATES_BULLHORN_BULK_JOB_SUBMISSIONS_CREATE_ERROR,
  BULLHORN_FIELD_OPTIONS_REQUEST,
  BULLHORN_FIELD_OPTIONS_SUCCESS,
  BULLHORN_FIELD_OPTIONS_ERROR,
  BULLHORN_CORPORATE_USERS_REQUEST,
  BULLHORN_CORPORATE_USERS_SUCCESS,
  BULLHORN_CORPORATE_USERS_ERROR,
} from './constants';

import { FlashDefinition } from './utils/flashDefinition';

type BullhornBulkResponse =
  | { status: string }
  | {
      // eslint-disable-next-line camelcase
      bullhorn_candidate_id: string;
      'created?': boolean;
      // eslint-disable-next-line camelcase
      candidate_id: string;
    };

export function searchBullhornJobs(searchTerm: string, markImported = false) {
  return (dispatch: AppDispatch) =>
    new Promise<JobResponse[]>((resolve, reject) => {
      dispatch({ type: SEARCH_JOBS_REQUEST });
      const query = qs.stringify({
        query: searchTerm,
        mark_imported: markImported,
      });

      httpGet(`/company/integrations/bullhorn/search_jobs?${query}`)
        .then((response) => {
          dispatch({
            type: SEARCH_JOBS_SUCCESS,
            payload: response,
          });

          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: SEARCH_JOBS_ERROR,
            error,
          });

          reject(error);
        });
    });
}

export function fetchBullhornJob(
  jobId: string,
  params: { sanitized?: boolean } = {}
) {
  return (dispatch: AppDispatch) =>
    new Promise<BullhornJobOrder>((resolve, reject) => {
      dispatch({ type: FETCH_BULLHORN_JOB_REQUEST });

      httpGet(
        `/company/integrations/bullhorn/job/${jobId}?${qs.stringify(params)}`
      )
        .then((response) => {
          dispatch({
            type: FETCH_BULLHORN_JOB_SUCCESS,
            payload: response,
          });

          resolve(response);
        })
        .catch((error) => {
          dispatch({ type: FETCH_BULLHORN_JOB_ERROR, error });

          reject(error);
        });
    });
}

export function fetchBullhornJobPrefill(jobId: string, boardId: string | null) {
  return (dispatch: AppDispatch) =>
    new Promise<BullhornJobPrefill>((resolve, reject) => {
      dispatch({ type: FETCH_BULLHORN_JOB_PREFILL_REQUEST });

      const query = buildStringifiedQuery(
        { board_id: boardId },
        { skipNulls: true }
      );

      httpGet(`/company/integrations/bullhorn/job/${jobId}/prefill?${query}`)
        .then((response) => {
          dispatch({
            type: FETCH_BULLHORN_JOB_PREFILL_SUCCESS,
            payload: response,
          });

          resolve(response);
        })
        .catch((error) => {
          dispatch({ type: FETCH_BULLHORN_JOB_PREFILL_ERROR });
          openFlashMessage(FlashDefinition.jobPrefillError);

          reject(error);
        });
    });
}

export function searchBullhornJobSubmissions(
  jobIds: string[],
  candidateIds?: string[]
) {
  return (dispatch: AppDispatch) =>
    new Promise<{
      submissions: { id: number; status: string; candidate: { id: number } }[];
    }>((resolve, reject) => {
      dispatch({ type: CANDIDATES_BULLHORN_JOB_SUBMISSIONS_SEARCH_REQUEST });

      const queryParams: Record<string, string[]> = {
        'job_ids[]': jobIds,
      };

      if (candidateIds && candidateIds.length > 0) {
        queryParams['candidate_ids[]'] = candidateIds;
      }

      const query = qs.stringify(queryParams, { indices: false });

      httpGet(`/company/integrations/bullhorn/search_job_submissions?${query}`)
        .then((response) => {
          dispatch({
            type: CANDIDATES_BULLHORN_JOB_SUBMISSIONS_SEARCH_SUCCESS,
            payload: response,
          });

          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: CANDIDATES_BULLHORN_JOB_SUBMISSIONS_SEARCH_ERROR,
            error,
          });

          reject(error);
        });
    });
}

export function createBullhornJobSubmission(
  jobId: string,
  candidateId: string | null,
  status: string,
  resourceType?: 'profile' | 'listing',
  resourceId?: string
) {
  return (dispatch: AppDispatch) =>
    new Promise<CandidateSubmissionResponse>((resolve, reject) => {
      dispatch({ type: CANDIDATES_BULLHORN_JOB_SUBMISSIONS_CREATE_REQUEST });

      httpPost('/company/integrations/bullhorn/create_job_submission', {
        status,
        job_id: jobId,
        candidate_id: candidateId,
        resource_type: resourceType,
        resource_id: resourceId,
      })
        .then((response) => {
          dispatch({
            type: CANDIDATES_BULLHORN_JOB_SUBMISSIONS_CREATE_SUCCESS,
            payload: response,
          });

          openFlashMessage(FlashDefinition.createSubmissionSuccess);

          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: CANDIDATES_BULLHORN_JOB_SUBMISSIONS_CREATE_ERROR,
            error,
          });

          reject(error);
        });
    });
}

export function createBulkBullhornSubmissions(
  status: string,
  jobId: string,
  candidateIds: string[],
  resourceType: 'profile' | 'listing'
) {
  return (dispatch: AppDispatch) =>
    new Promise<BullhornBulkResponse>((resolve, reject) => {
      dispatch({
        type: CANDIDATES_BULLHORN_BULK_JOB_SUBMISSIONS_CREATE_REQUEST,
      });
      httpPost(`/company/integrations/bullhorn/create_bulk_job_submissions`, {
        status,
        job_id: jobId,
        candidate_ids: candidateIds,
        resource_type: resourceType,
      })
        .then((response) => {
          dispatch({
            type: CANDIDATES_BULLHORN_BULK_JOB_SUBMISSIONS_CREATE_SUCCESS,
            payload: response,
          });

          if (response.bullhorn_candidate_id) {
            openFlashMessage(FlashDefinition.createSubmissionSuccess);
          } else {
            openFlashMessage(FlashDefinition.createBulkSubmissionProcessing);
          }

          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: CANDIDATES_BULLHORN_BULK_JOB_SUBMISSIONS_CREATE_ERROR,
          });

          reject(error);
        });
    });
}

export function searchBullhornTearSheets(searchTerm: string) {
  return (dispatch: AppDispatch) =>
    new Promise<{
      tearsheets: { id: number; name: string; isDeleted?: boolean }[];
    }>((resolve, reject) => {
      dispatch({ type: CANDIDATES_BULLHORN_TEAR_SHEETS_SEARCH_REQUEST });
      const query = qs.stringify({ query: searchTerm });
      httpGet(`/company/integrations/bullhorn/search_tearsheets?${query}`)
        .then((response) => {
          dispatch({
            type: CANDIDATES_BULLHORN_TEAR_SHEETS_SEARCH_SUCCESS,
            payload: response,
          });
          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: CANDIDATES_BULLHORN_TEAR_SHEETS_SEARCH_ERROR,
            error,
          });
          reject(error);
        });
    });
}

export function addCandidatesToBullhornTearsheets(
  tearsheetIds: number[],
  candidateIds: string[]
) {
  return (dispatch: AppDispatch) =>
    new Promise<BullhornBulkResponse>((resolve, reject) => {
      dispatch({
        type: CANDIDATES_BULLHORN_TEAR_SHEETS_ADD_CANDIDATE_REQUEST,
      });

      httpPost('/company/integrations/bullhorn/add_candidates_to_tearsheets', {
        tearsheet_ids: tearsheetIds,
        candidate_ids: candidateIds,
        resource_type: 'profile',
      })
        .then((response) => {
          dispatch({
            type: CANDIDATES_BULLHORN_TEAR_SHEETS_ADD_CANDIDATE_SUCCESS,
            payload: response,
          });

          if (response.bullhorn_candidate_id) {
            openFlashMessage(FlashDefinition.addCandidateToTearsheetSuccess);
          } else {
            openFlashMessage(FlashDefinition.addCandidateToTearsheetProcessing);
          }

          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: CANDIDATES_BULLHORN_TEAR_SHEETS_ADD_CANDIDATE_ERROR,
            error,
          });

          reject(error);
        });
    });
}

export function createBullhornTearsheet(params: {
  name: string;
  description?: string;
  // eslint-disable-next-line camelcase
  is_private?: boolean;
}) {
  return (dispatch: AppDispatch) =>
    new Promise<{ id: number; name: string }>((resolve, reject) => {
      dispatch({ type: CANDIDATES_BULLHORN_TEAR_SHEETS_CREATE_REQUEST });

      httpPost('/company/integrations/bullhorn/create_tearsheet', params)
        .then((response) => {
          dispatch({
            type: CANDIDATES_BULLHORN_TEAR_SHEETS_CREATE_SUCCESS,
            payload: response,
          });

          openFlashMessage(FlashDefinition.createTearsheetSuccess);
          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: CANDIDATES_BULLHORN_TEAR_SHEETS_CREATE_ERROR,
            error,
          });

          openFlashMessage(FlashDefinition.createTearsheetError);
          reject(error);
        });
    });
}

type BullhornJobSearchOptions = {
  jobDescriptionSource?: JobDescriptionPickable;
  prevSearchId?: string;
};

export function createCandidateSearchFromBullhornJob(
  bullhornJobId: string,
  options: BullhornJobSearchOptions = {}
) {
  return (dispatch: AppDispatch) =>
    new Promise<CandidateSearchEntry>((resolve, reject) => {
      dispatch({ type: CREATE_CANDIDATE_SEARCH_FROM_BULLHORN_JOB_REQUEST });

      httpPost('/candidates/searches', {
        bullhorn_job_id: bullhornJobId,
        job_description_source: options.jobDescriptionSource,
        prev_search_id: options.prevSearchId,
      })
        .then((response) => {
          dispatch({
            type: CREATE_CANDIDATE_SEARCH_FROM_BULLHORN_JOB_SUCCESS,
            payload: response,
          });
          resolve(response);
        })
        .catch((error) => {
          dispatch({
            type: CREATE_CANDIDATE_SEARCH_FROM_BULLHORN_JOB_ERROR,
            error,
          });
          reject(error);
        });
    });
}

export function fetchBullhornFieldOptions(
  optionsType: string,
  params: { query?: string; ids?: string[] }
) {
  return (dispatch: AppDispatch) =>
    new Promise<CandidateFieldOption[]>((resolve, reject) => {
      dispatch({ type: BULLHORN_FIELD_OPTIONS_REQUEST });

      httpPost(`/company/integrations/bullhorn/options/${optionsType}`, params)
        .then((response: { options: BullhornFieldOption[] }) => {
          dispatch({
            type: BULLHORN_FIELD_OPTIONS_SUCCESS,
            payload: response,
          });

          resolve(
            (response.options || []).map(
              ({ private_label: privateLabel, ...option }) => ({
                ...option,
                value: String(option.value),
                hint: privateLabel,
              })
            )
          );
        })
        .catch((error) => {
          dispatch({
            type: BULLHORN_FIELD_OPTIONS_ERROR,
            error,
          });

          reject(error);
        });
    });
}

export function fetchBullhornCorporateUsers(params: {
  query?: string;
  ids?: number[];
}) {
  return (dispatch: AppDispatch) =>
    new Promise<BullhornCorporateUserOption[]>((resolve, reject) => {
      dispatch({ type: BULLHORN_CORPORATE_USERS_REQUEST });

      const query = buildStringifiedQuery(params, { arrayFormat: 'brackets' });

      httpGet(`/company/integrations/bullhorn/corporate_users?${query}`)
        .then((response: { options: BullhornCorporateUserOption[] }) => {
          dispatch({ type: BULLHORN_CORPORATE_USERS_SUCCESS });
          resolve(response.options || []);
        })
        .catch((error) => {
          dispatch({ type: BULLHORN_CORPORATE_USERS_ERROR, error });
          reject(error);
        });
    });
}
